import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import AppErrorBoundary from "./components/AppErrorBoundary";
import AccreditedInvestorGate from "./components/AccreditedInvestorGate";
import AppBootProfileDocumentGate from "./components/AppBootProfileDocumentGate";
import AppBootProfileProviderBranch from "./components/AppBootProfileProviderBranch";
import DashboardLayout from "./components/DashboardLayout";
import { RealtimeProvider } from "./contexts/RealtimeContext";
import { NotificationsProvider } from "./contexts/NotificationsContext";
import { IbkrStatusProvider } from "./contexts/IbkrStatusContext";
import EnvironmentBanner from "./components/EnvironmentBanner";
import ProtectedRoute from "./components/ProtectedRoute";
import { legacyNotificationsRedirectPath } from "./pages/admin/notifications/legacyRedirect";
import AlphaAccessGuard from "./components/alpha/AlphaAccessGuard";
import AlphaSetupCompatibilityRedirect, { LEGACY_SETUP_REDIRECT_PATHS } from "./components/alpha/AlphaSetupCompatibilityRedirect";
import SetupV2AccessGuard from "./components/alpha/SetupV2AccessGuard";
import RouteTracker from "./components/RouteTracker";
import SessionMilestoneTracker from "./components/SessionMilestoneTracker";
import ScrollToTop from "./components/ScrollToTop";
import StaticPageHead from "./seo/StaticPageHead";
import AdminCommandPaletteHost from "./pages/admin/components/AdminCommandPaletteHost";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ToastProvider } from "./contexts/ToastContext";
import LLMsPage from "./pages/LLMsPage";
import ClaudeConnectorArticleRedirect from "./pages/ClaudeConnectorArticleRedirect";
import OTPLoginPage from "./pages/OTPLoginPage";
import AuthPageV2 from "./pages/auth/AuthPageV2";
import OAuthConsentPage from "./pages/auth/OAuthConsentPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import SignupPage from "./pages/SignupPage";
import TermsOfUsePage from "./pages/TermsOfUsePage";
import configService from "./services/configService";
import { getAlphaDestinationForLegacyInvestorPath } from "./utils/investorRouteCutover";
import { getOwnProviderId } from "./utils/providerIdentity";
import { getPublicProviderProfilePath } from "./config/publicProviderProfiles";

// Provider pages
import PicksRouter from "./components/PicksRouter";
import ProviderLayoutV3 from "./components/provider/ProviderLayoutV3";
import ProductSharePageV2 from "./pages/ProductSharePageV2";
import ProductShareDevPreviewPage from "./pages/ProductShareDevPreviewPage";
import ProviderDashboardPageV3 from "./pages/provider/ProviderDashboardPageV3";

// Advisor pages
import AdvisorLayout from "./components/advisor/AdvisorLayout";
import AdvisorClientsPage from "./pages/advisor/AdvisorClientsPage";
import AdvisorClientsResolver from "./pages/advisor/AdvisorClientsResolver";
import AdvisorGroupsPage from "./pages/advisor/AdvisorGroupsPage";
import AdvisorGroupsResolver from "./pages/advisor/AdvisorGroupsResolver";
import AdvisorGroupDetailPage from "./pages/advisor/AdvisorGroupDetailPage";

// Admin pages
import AbuseStatsPage from "./pages/admin/AbuseStatsPage";
import AdminCommentsPage from "./pages/admin/AdminCommentsPage";
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import AdminBulkStrategiesPage from "./pages/admin/AdminBulkStrategiesPage";
import AdminProductsPage from "./pages/admin/AdminProductsPage/AdminProductsPage";
import AdminSubscriptionsPage from "./pages/admin/AdminSubscriptionsPage";
import BasketMinAnalyzerPage from "./pages/admin/BasketMinAnalyzerPage/BasketMinAnalyzerPage";
import AdminProductCurationPage from "./pages/admin/AdminProductCurationPage";
import AdminReportingPage from "./pages/admin/AdminReportingPage";
import AdminUserDashboardPage from "./pages/admin/AdminUserDashboardPage";
import AdminUserStrategyPage from "./pages/admin/AdminUserStrategyPage";
import AdvisorAccountsPage from "./pages/admin/AdvisorAccountsPage";
import CitriniImportDetailPage from "./pages/admin/CitriniImportDetailPage";
import CitriniImportPage from "./pages/admin/CitriniImportPage";
import ContractsPage from "./pages/admin/ContractsPage";
import DocumentManagerPage from "./pages/admin/DocumentManagerPage";
import EssentialInstrumentsPage from "./pages/admin/EssentialInstrumentsPage";
import ProviderProfilesPage from "./pages/admin/ProviderProfilesPage";
import ProviderSubscriptionsPage from "./pages/admin/ProviderSubscriptionsPage";
import ProvidersPageAdmin from "./pages/admin/ProvidersPage";
import QuestionsPage from "./pages/admin/QuestionsPage";
import ServicesHealthPage from "./pages/admin/ServicesHealthPage";
import OrchestrationStatusPage from "./pages/admin/OrchestrationStatusPage";
import AppSettingsPage from "./pages/admin/AppSettingsPage";
import UserSettingsRegistryPage from "./pages/admin/UserSettingsRegistryPage";
import UsersPage from "./pages/admin/UsersPage";
import AdminCustomerViewPage from "./pages/admin/AdminCustomerViewPage";
import JobsDashboard from "./pages/admin/jobs/JobsDashboard";

// Base route handler
import BaseRouteHandler from "./components/BaseRouteHandler";

import { Suspense } from "react";
import {
  shouldDiscardAlphaAdoptMixRouteState,
  stripAlphaAdoptMixRouteState,
} from "./pages/alpha/alphaAnalyzerAdopt";

// V3 lazy-loaded pages (code-split behind V3 gate)
const ProductsListPageV3 = React.lazy(() => import("./pages/provider/ProductsListPageV3/ProductsListPageV3"));
const BasketCurationPageV3 = React.lazy(() => import("./pages/provider/BasketCurationPageV3/BasketCurationPageV3"));
const PortfolioCurationPageV3 = React.lazy(() => import("./pages/provider/PortfolioCurationPageV3/PortfolioCurationPageV3"));
const OptionsScreenerPageV3 = React.lazy(() => import("./pages/provider/OptionsScreenerPageV3/OptionsScreenerPageV3"));
const ProviderEarningsPageV3 = React.lazy(() => import("./pages/provider/ProviderEarningsPageV3"));
const ProviderProfilePage = React.lazy(() => import("./pages/provider/ProviderProfilePage/ProviderProfilePage"));
const PublicProviderProfilePage = React.lazy(() => import("./pages/PublicProviderProfilePage"));
const StrategyAnalyzerPage = React.lazy(() => import("./pages/dashboard/StrategyAnalyzerPage"));
const ClientOnboardingV2Page = React.lazy(
  () => import("./pages/admin/ClientOnboardingV2Page/ClientOnboardingV2Page")
);
const AccountLinkingPage = React.lazy(
  () => import("./pages/admin/AccountLinkingPage")
);
const AccountingPage = React.lazy(
  () => import("./pages/admin/AccountingPage")
);
const InvestorRebalanceEventsPage = React.lazy(() => import("./pages/admin/InvestorRebalanceEventsPage"));
const TradingErrorExportPage = React.lazy(() => import("./pages/admin/TradingErrorExportPage"));
const UserContractErrorExclusionsPage = React.lazy(() => import("./pages/admin/UserContractErrorExclusionsPage"));
const RebalanceSignalsPage = React.lazy(() => import("./pages/admin/RebalanceSignalsPage"));
const InvestorRebalanceEventDetailPage = React.lazy(() => import("./pages/admin/InvestorRebalanceEventDetailPage"));
const InvestorRebalanceEventUserDetailPage = React.lazy(() => import("./pages/admin/InvestorRebalanceEventUserDetailPage"));
const VerificationReportPage = React.lazy(() => import("./pages/admin/VerificationReportPage"));
const StockOverridesPage = React.lazy(() => import("./pages/admin/overrides/StockOverridesPage"));
const ContractVerificationsPage = React.lazy(() => import("./pages/admin/ContractVerificationsPage"));
const ContractRemarksPage = React.lazy(() => import("./pages/admin/ContractRemarksPage"));
const FxSweepPage = React.lazy(() => import("./pages/admin/FxSweepPage"));
const FxSweepUserDetailPage = React.lazy(() => import("./pages/admin/FxSweepUserDetailPage"));
const OptionsTradingPage = React.lazy(() => import("./pages/admin/OptionsTradingPage"));
const OptionsTradingEventDetailPage = React.lazy(() => import("./pages/admin/OptionsTradingEventDetailPage"));
const OptionsTradingUserDetailPage = React.lazy(() => import("./pages/admin/OptionsTradingUserDetailPage"));
const ExchangesPage = React.lazy(() => import("./pages/admin/ExchangesPage"));
const RateCardsPage = React.lazy(() => import("./pages/admin/RateCardsPage"));
const ManagedStrategiesPage = React.lazy(() => import("./pages/admin/ManagedStrategiesPage/ManagedStrategiesPage"));
const ManagedStrategyDetailPage = React.lazy(() => import("./pages/admin/ManagedStrategiesPage/ManagedStrategyDetailPage"));
const AdminManagedStrategyProductPage = React.lazy(
  () =>
    import(
      "./pages/admin/AdminManagedStrategyProductPage/AdminManagedStrategyProductPage"
    )
);
const ChatbotPromptsPage = React.lazy(() => import("./pages/admin/ChatbotPromptsPage"));
const AlphaExperience = React.lazy(() => import("./pages/alpha/AlphaExperience"));
const SetupV2Experience = React.lazy(() => import("./pages/alpha/setupv2/SetupV2Experience"));
const ArticlesPage = React.lazy(() => import("./pages/admin/ArticlesPage"));
const ArticleEditorPage = React.lazy(() => import("./pages/admin/ArticleEditorPage"));
const SitemapPage = React.lazy(() => import("./pages/admin/SitemapPage"));
const AdminNotificationsPage = React.lazy(() => import("./pages/admin/notifications/AdminNotificationsPage"));

// Create QueryClient with default options for account data caching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
      gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/** PWA-2748: `/admin/notifications` moved to per-view routes; legacy `?tab=` links redirect. */
function NotificationsLegacyRedirect() {
  const location = useLocation();
  return <Navigate to={legacyNotificationsRedirectPath(location.search)} replace />;
}

function NavigateToProducts({ type }: { type: "basket" | "portfolio" }) {
  const { providerId } = useParams<{ providerId: string }>();
  if (!providerId) return <Navigate to="/admin/products" replace />;

  return (
    <Navigate
      to={`/admin/products?providerId=${encodeURIComponent(providerId)}&type=${type}`}
      replace
    />
  );
}

function NavigateToCuration({ productType }: { productType: "basket" | "portfolio" }) {
  const { providerId, basketId, portfolioId } = useParams<{
    providerId: string;
    basketId?: string;
    portfolioId?: string;
  }>();
  const productId = productType === "basket" ? basketId : portfolioId;
  if (!providerId || !productId) return <Navigate to="/admin/products" replace />;

  return (
    <Navigate
      to={`/admin/products/${productType}s/${encodeURIComponent(providerId)}/${encodeURIComponent(productId)}`}
      replace
    />
  );
}

function AlphaActivityCompatibilityRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={{
        pathname: "/alpha/activity",
        search: location.search,
        hash: location.hash,
      }}
      replace
      state={location.state}
    />
  );
}

function RetiredHomepagePreviewNotFound() {
  return (
    <main className="min-h-screen bg-gray-50 p-6 transition-colors duration-300 dark:bg-black md:p-8">
      <div className="rounded-2xl border border-gray-200 bg-white py-24 text-center dark:border-rp-separator dark:bg-rp-surface">
        <h1 className="text-lg font-bold text-gray-900 dark:text-white">Page not found</h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">The page you're looking for doesn't exist.</p>
        <a href="/" className="mt-5 inline-block rounded-lg bg-rp-amber px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-yellow-600">Go home</a>
      </div>
    </main>
  );
}

/** Provider-portal own profile: renders inside the provider shell for the owning provider. */
function OwnProviderProfile() {
  const { user } = useAuth();
  const ownProviderId = getOwnProviderId(user);
  if (!ownProviderId) return <Navigate to="/provider/dashboard" replace />;
  return (
    <ProviderLayoutV3>
      <Suspense fallback={null}>
        <ProviderProfilePage providerIdOverride={ownProviderId} />
      </Suspense>
    </ProviderLayoutV3>
  );
}

function AlphaAccountContextBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const {
    effectiveUserId,
    isAccountContextLoading,
    isAccountSwitching,
    refreshAccounts,
  } = useAuth();
  const previousEffectiveUserIdRef = React.useRef(effectiveUserId);
  const accountChanged = previousEffectiveUserIdRef.current !== null
    && effectiveUserId !== null
    && previousEffectiveUserIdRef.current !== effectiveUserId;
  const invalidAdoptRouteState = Boolean(effectiveUserId)
    && shouldDiscardAlphaAdoptMixRouteState(location.state, effectiveUserId);
  if (accountChanged || invalidAdoptRouteState) {
    previousEffectiveUserIdRef.current = effectiveUserId;
    return (
      <Navigate
        to={{
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
        }}
        replace
        state={accountChanged
          ? undefined
          : stripAlphaAdoptMixRouteState(location.state)}
      />
    );
  }

  if (effectiveUserId) {
    previousEffectiveUserIdRef.current = effectiveUserId;
  }

  const accountContextBlocked = isAccountContextLoading || isAccountSwitching;

  if (!effectiveUserId && accountContextBlocked) {
    return (
      <div className="grid h-dvh place-items-center bg-v7-canvas font-v7-sans text-v7-ink">
        <span className="animate-pulse text-sm text-v7-ink/60" role="status">
          {isAccountSwitching ? "Switching account" : "Loading account"}
        </span>
      </div>
    );
  }

  if (!effectiveUserId) {
    return (
      <div className="grid h-dvh place-items-center bg-v7-canvas px-6 font-v7-sans text-v7-ink">
        <div className="max-w-sm text-center">
          <h1 className="text-lg font-bold">Account context unavailable</h1>
          <p className="mt-2 text-sm text-v7-ink-soft">
            Account-owned data is hidden until the selected account can be verified.
          </p>
          <button
            type="button"
            onClick={() => void refreshAccounts()}
            className="mt-4 min-h-11 rounded-v7-control bg-v7-bronze-dark px-4 text-sm font-semibold text-v7-white transition-colors hover:bg-v7-bronze"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-dvh">
      <div
        aria-hidden={accountContextBlocked || undefined}
        inert={accountContextBlocked || undefined}
        className={accountContextBlocked
          ? "invisible min-h-dvh pointer-events-none select-none"
          : "min-h-dvh"}
      >
        <React.Fragment key={effectiveUserId}>{children}</React.Fragment>
      </div>
      {accountContextBlocked && (
        <div className="absolute inset-0 z-50 grid min-h-dvh place-items-center bg-v7-canvas font-v7-sans text-v7-ink">
          <span className="animate-pulse text-sm text-v7-ink/60" role="status">
            {isAccountSwitching ? "Switching account" : "Loading account"}
          </span>
        </div>
      )}
    </div>
  );
}

/** Public provider profiles use the homepage theme for every visitor. */
function PublicProviderProfileRoute() {
  const { providerId } = useParams<{ providerId: string }>();
  const location = useLocation();
  const canonicalPath = providerId && /^\d+$/.test(providerId)
    ? getPublicProviderProfilePath(Number(providerId))
    : null;

  if (canonicalPath) {
    return <Navigate to={`${canonicalPath}${location.search}${location.hash}`} replace />;
  }

  return (
    <Suspense fallback={<div className="min-h-dvh bg-v7-paper" />}>
      <PublicProviderProfilePage />
    </Suspense>
  );
}

function AdminUserAnalyzerRoute() {
  const { userId } = useParams<{ userId: string }>();
  if (!userId) return <Navigate to="/admin/users" replace />;

  return (
    <Suspense fallback={null}>
      <StrategyAnalyzerPage adminUserId={userId} />
    </Suspense>
  );
}

function NavigateToCustomerViewDashboard() {
  const { viewKey } = useParams<{ viewKey: string }>();
  if (!viewKey) return <Navigate to="/admin/users" replace />;
  return <Navigate to={`/admin/customer-view/${viewKey}/dashboard`} replace />;
}

function LegacyInvestorRouteRedirect() {
  const location = useLocation();
  const destination = getAlphaDestinationForLegacyInvestorPath(location.pathname) ?? "/alpha";

  return (
    <ProtectedRoute>
      <Navigate
        to={{
          pathname: destination,
          search: location.search,
          hash: location.hash,
        }}
        replace
      />
    </ProtectedRoute>
  );
}

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <Router>
              <AppBootProfileDocumentGate>
                <AppBootProfileProviderBranch>
              <RealtimeProvider>
              <NotificationsProvider>
              <IbkrStatusProvider>
              <ScrollToTop />
              <StaticPageHead />
              <RouteTracker />
              <SessionMilestoneTracker />
              <AdminCommandPaletteHost />
              <EnvironmentBanner environment={configService.getEnvironment()} />
              <div className="App">
                <AppErrorBoundary>
                <Routes>
                  <Route path="/" element={<BaseRouteHandler />} />
                  <Route path="/home-v2" element={<RetiredHomepagePreviewNotFound />} />
                  <Route path="/login" element={<OTPLoginPage />} />
                  <Route path="/auth/consent" element={<OAuthConsentPage />} />
                  <Route path="/auth/select-account" element={<OAuthConsentPage selectAccount />} />
                  <Route path="/mm-opm/login" element={<AuthPageV2 mode="login" branding="mm-opm" />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms" element={<TermsOfUsePage />} />
                  <Route path="/llms" element={<LLMsPage />} />
                  <Route path="/ai" element={<ClaudeConnectorArticleRedirect />} />
                  <Route path="/articles/claude-connector" element={<ClaudeConnectorArticleRedirect />} />
                  <Route path="/connector" element={<Navigate to="/ai" replace />} />
                  <Route path="/mcp" element={<Navigate to="/ai" replace />} />

                  {/* Public Shareable Links */}
                  <Route path="/product-share/dev-preview" element={<ProductShareDevPreviewPage />} />
                  <Route path="/product-share/:token" element={<ProductSharePageV2 />} />
                  <Route path="/picks/:token" element={<PicksRouter />} />

                  {/* Investor V2. Authentication and investor-role access remain
                      separate so provider/advisor-only accounts keep their portals. */}
                  <Route
                    path="/alpha-activity"
                    element={
                      <ProtectedRoute>
                        <AlphaAccessGuard>
                          <AlphaActivityCompatibilityRedirect />
                        </AlphaAccessGuard>
                      </ProtectedRoute>
                    }
                  />
                  {LEGACY_SETUP_REDIRECT_PATHS.map(path => <Route key={path} path={path} element={<ProtectedRoute><AlphaAccessGuard><AlphaSetupCompatibilityRedirect /></AlphaAccessGuard></ProtectedRoute>} />)}
                  <Route
                    path="/alpha/setupv2/*"
                    element={(
                      <ProtectedRoute>
                      <SetupV2AccessGuard>
                        <AccreditedInvestorGate>
                          <div className="h-dvh">
                            <Suspense fallback={<p role="status">Loading setup</p>}>
                              <SetupV2Experience />
                            </Suspense>
                          </div>
                        </AccreditedInvestorGate>
                      </SetupV2AccessGuard>
                      </ProtectedRoute>
                    )}
                  />
                  <Route
                    path="/alpha/*"
                    element={
                      <ProtectedRoute>
                        <AlphaAccessGuard>
                          <AlphaAccountContextBoundary>
                            <AccreditedInvestorGate>
                            {/* AlphaLayout sizes off its parent (md:h-full), so the
                                Alpha tree gets its own viewport-height scope here
                                instead of constraining the app-wide layout. */}
                            <div className="h-dvh">
                              <Suspense
                                fallback={(
                                  <div className="grid h-full min-h-0 place-items-center bg-v7-canvas font-v7-sans text-v7-ink">
                                    <span className="animate-pulse text-sm text-v7-ink/60" role="status">Loading Alpha</span>
                                  </div>
                                )}
                              >
                                <AlphaExperience />
                              </Suspense>
                            </div>
                            </AccreditedInvestorGate>
                          </AlphaAccountContextBoundary>
                        </AlphaAccessGuard>
                      </ProtectedRoute>
                    }
                  />

                  {/* Retired investor routes remain as authenticated replace
                      redirects only. No legacy investor page can mount. */}
                  {[
                    "/positions",
                    "/allocations",
                    "/strategy",
                    "/strategy/analyze",
                    "/positions/discover",
                    "/positions/discover/:productType/:providerId/:productId",
                    "/positions/activity",
                    "/positions/settings",
                    "/settings",
                    "/positions/profile",
                    "/positions/notifications",
                    "/positions/onboarding",
                    "/dashboard",
                    "/dashboard/*",
                  ].map((path) => (
                    <Route key={path} path={path} element={<LegacyInvestorRouteRedirect />} />
                  ))}

                  {/* Anonymous public profile. Editing remains on the protected provider/admin routes. */}
                  <Route
                    path="/providers"
                    element={<PublicProviderProfileRoute />}
                  />
                  <Route
                    path="/providers/:providerSlug"
                    element={<PublicProviderProfileRoute />}
                  />
                  <Route
                    path="/providers/:providerId/profile"
                    element={<PublicProviderProfileRoute />}
                  />

                  {/* Provider Routes */}
                  <Route
                    path="/provider/dashboard"
                    element={
                      <ProtectedRoute requireAnyRole={["PROVIDER", "ADMIN"]}>
                        <Suspense fallback={null}><ProviderLayoutV3><ProviderDashboardPageV3 /></ProviderLayoutV3></Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/provider/portfolios"
                    element={
                      <ProtectedRoute requireAnyRole={["PROVIDER", "ADMIN"]}>
                        <Suspense fallback={null}><ProviderLayoutV3><ProductsListPageV3 /></ProviderLayoutV3></Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/provider/portfolios/curate/:portfolioId"
                    element={
                      <ProtectedRoute requireAnyRole={["PROVIDER", "ADMIN"]}>
                        <Suspense fallback={null}><ProviderLayoutV3><PortfolioCurationPageV3 /></ProviderLayoutV3></Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/provider/baskets"
                    element={
                      <ProtectedRoute requireAnyRole={["PROVIDER", "ADMIN"]}>
                        <Suspense fallback={null}><ProviderLayoutV3><ProductsListPageV3 /></ProviderLayoutV3></Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/provider/baskets/create"
                    element={
                      <ProtectedRoute requireAnyRole={["PROVIDER", "ADMIN"]}>
                        <Suspense fallback={null}><ProviderLayoutV3><BasketCurationPageV3 /></ProviderLayoutV3></Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/provider/baskets/edit/:basketId"
                    element={
                      <ProtectedRoute requireAnyRole={["PROVIDER", "ADMIN"]}>
                        <Suspense fallback={null}><ProviderLayoutV3><BasketCurationPageV3 /></ProviderLayoutV3></Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/provider/options"
                    element={
                      <ProtectedRoute requireAnyRole={["PROVIDER", "ADMIN"]}>
                        <Suspense fallback={null}><ProviderLayoutV3><OptionsScreenerPageV3 /></ProviderLayoutV3></Suspense>
                      </ProtectedRoute>
                    }
                  />
                  {/* PWA-2369: provider earnings. The API is provider self-service
                      (requireAnyRole(['PROVIDER']) + resolveUserProvider), so the route
                      requires the PROVIDER role -- a bare ADMIN would only 403. The
                      sidebar link is admin-gated for now (see ProviderSidebarV3). */}
                  <Route
                    path="/provider/earnings"
                    element={
                      <ProtectedRoute requireAnyRole={["PROVIDER"]}>
                        <Suspense fallback={null}><ProviderLayoutV3><ProviderEarningsPageV3 /></ProviderLayoutV3></Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/provider/profile"
                    element={
                      <ProtectedRoute requireAnyRole={["PROVIDER", "ADMIN"]}>
                        <OwnProviderProfile />
                      </ProtectedRoute>
                    }
                  />
                  {/* Advisor Routes */}
                  <Route
                    path="/advisor/clients"
                    element={
                      <ProtectedRoute requireAnyRole={["ADVISOR", "ADMIN"]}>
                        <AdvisorLayout><AdvisorClientsResolver /></AdvisorLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/advisor/:advisorAccountId/clients"
                    element={
                      <ProtectedRoute requireAnyRole={["ADVISOR", "ADMIN"]}>
                        <AdvisorLayout><AdvisorClientsPage /></AdvisorLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/advisor/groups"
                    element={
                      <ProtectedRoute requireAnyRole={["ADVISOR", "ADMIN"]}>
                        <AdvisorLayout><AdvisorGroupsResolver /></AdvisorLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/advisor/:advisorAccountId/groups"
                    element={
                      <ProtectedRoute requireAnyRole={["ADVISOR", "ADMIN"]}>
                        <AdvisorLayout><AdvisorGroupsPage /></AdvisorLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/advisor/:advisorAccountId/groups/:groupId"
                    element={
                      <ProtectedRoute requireAnyRole={["ADVISOR", "ADMIN"]}>
                        <AdvisorLayout><AdvisorGroupDetailPage /></AdvisorLayout>
                      </ProtectedRoute>
                    }
                  />
                  {/* Admin Routes */}
                  <Route
                    path="/admin/notifications"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <NotificationsLegacyRedirect />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/notifications/:view"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <AdminNotificationsPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/dashboard"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <AdminDashboardPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/reporting"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <AdminReportingPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/users"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <UsersPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/account-linking"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <AccountLinkingPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/managed-users"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Navigate to="/admin/account-linking?tab=managed-user-pool" replace />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/user-access-grants"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Navigate to="/admin/account-linking?tab=user-access-grants" replace />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/contract-error-exclusions"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <UserContractErrorExclusionsPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/users/:userId/dashboard"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <AdminUserDashboardPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/customer-view/:viewKey"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <AdminCustomerViewPage />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<NavigateToCustomerViewDashboard />} />
                    <Route path="dashboard" element={<></>} />
                    <Route path="positions" element={<></>} />
                    <Route path="profile" element={<></>} />
                    <Route path="*" element={<NavigateToCustomerViewDashboard />} />
                  </Route>
                  <Route
                    path="/admin/users/:userId/strategy"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <AdminUserStrategyPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/users/:userId/analyze"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <AdminUserAnalyzerRoute />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/bulk-strategies"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <AdminBulkStrategiesPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/providers"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <ProvidersPageAdmin />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/provider-profiles"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <ProviderProfilesPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  {/*
                    Articles (PWA-2546). `/new` must precede `/:id`, or React
                    Router matches "new" as an article id.
                  */}
                  <Route
                    path="/admin/articles"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <ArticlesPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/articles/new"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <ArticleEditorPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/articles/:id"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <ArticleEditorPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  {/* Sitemap (PWA-2550). Curated sitemap.xml links plus a preview. */}
                  <Route
                    path="/admin/sitemap"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <SitemapPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/contracts"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <ContractsPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/contract-remarks"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <ContractRemarksPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/essential-instruments"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <EssentialInstrumentsPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/min-analyzer/:productType/:providerId/:productId"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <BasketMinAnalyzerPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/providers/:providerId/baskets"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <NavigateToProducts type="basket" />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/providers/:providerId/baskets/:basketId"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <NavigateToCuration productType="basket" />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/providers/:providerId/portfolios"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <NavigateToProducts type="portfolio" />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/providers/:providerId/portfolios/:portfolioId"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <NavigateToCuration productType="portfolio" />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/providers/:providerId/subscriptions"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <ProviderSubscriptionsPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/subscriptions"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <AdminSubscriptionsPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/questions"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <QuestionsPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/abuse"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <AbuseStatsPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/comments"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <AdminCommentsPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/rate-cards"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <RateCardsPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/managed-strategies"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <ManagedStrategiesPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/managed-strategies/:id"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <ManagedStrategyDetailPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/accounting"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <AccountingPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/ledger"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Navigate to="/admin/accounting?tab=ledger" replace />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/provider-earnings"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Navigate to="/admin/accounting?tab=provider-earnings" replace />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/provider-payments"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Navigate to="/admin/accounting?tab=provider-payments" replace />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/jobs"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <JobsDashboard />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/advisor-accounts"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <AdvisorAccountsPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/client-onboarding"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <ClientOnboardingV2Page />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/client-onboarding-v2"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Navigate to="/admin/client-onboarding" replace />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/investor-rebalance-events"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <InvestorRebalanceEventsPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/investor-rebalance-events/:eventId"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <InvestorRebalanceEventDetailPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/trading-error-export"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <TradingErrorExportPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/investor-rebalance-events/:eventId/users/:userId"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <InvestorRebalanceEventUserDetailPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/rebalance-signals"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <RebalanceSignalsPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/verification-reports"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <VerificationReportPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/stock-overrides"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <StockOverridesPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/services-health"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <ServicesHealthPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/orchestration-status"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <OrchestrationStatusPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/documents"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <DocumentManagerPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/citrini-import"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <CitriniImportPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/citrini-import/:fetchInfoId"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <CitriniImportDetailPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/fx-sweep"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <FxSweepPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/fx-sweep/:userId"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <FxSweepUserDetailPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/options-trading"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <OptionsTradingPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/options-trading/:eventId"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <OptionsTradingEventDetailPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/options-trading/:eventId/users/:userId"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <OptionsTradingUserDetailPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/products"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <AdminProductsPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/products/baskets/:providerId/:basketId"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <AdminProductCurationPage mode="basket" />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/products/portfolios/:providerId/:portfolioId"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <AdminProductCurationPage mode="portfolio" />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/products/managed-strategies/:strategyId"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <AdminManagedStrategyProductPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/admin/contract-verifications"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <ContractVerificationsPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/admin/exchanges"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <ExchangesPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/admin/app-settings"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <AppSettingsPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/admin/chatbot-prompts"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <Suspense fallback={null}>
                            <ChatbotPromptsPage />
                          </Suspense>
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/admin/user-settings"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <DashboardLayout>
                          <UserSettingsRegistryPage />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Redirect any unknown routes to home */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </AppErrorBoundary>
              </div>
              </IbkrStatusProvider>
              </NotificationsProvider>
              </RealtimeProvider>
                </AppBootProfileProviderBranch>
              </AppBootProfileDocumentGate>
            </Router>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
